/**
 * Force-change-password screen — surfaced both from /settings and from
 * the auth guard whenever `me.must_change_password` is true.
 *
 * Task 18 §2.4: live policy checklist + Zod refine mirroring backend
 * `password_policy.py`. Source of truth on backend; this UI is best-effort
 * UX feedback (server still validates on submit and returns
 * `400 {code: "password_policy", ...}` if drift sneaks in).
 */
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AppShell } from "@/layout/AppShell";
import { changePassword, logout } from "@/api/auth";
import {
  apiErrorFormIssue,
  FormErrorSummary,
  FormFieldError,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import { useInvalidateMe, useMe } from "@/hooks/useMe";
import { useSession } from "@/store/session";
import { evaluatePassword, MIN_CLASSES, type PasswordPolicyResult } from "@/lib/passwordPolicy";
import { cn } from "@/lib/utils";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

const schema = z
  .object({
    current_password: z.string().min(1),
    new_password: z.string().refine((v) => evaluatePassword(v).ok, { message: "policy" }),
    confirm: z.string().min(1),
  })
  .refine((data) => data.new_password === data.confirm, {
    path: ["confirm"],
    message: "mismatch",
  });

type FormValues = z.infer<typeof schema>;

export function ChangePasswordPage(): React.ReactElement {
  const { t } = useTranslation();
  const invalidateMe = useInvalidateMe();
  const navigate = useNavigate();
  const { user } = useMe();
  const clearSession = useSession((s) => s.clear);
  const forced = user?.must_change_password ?? false;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
    mode: "onChange",
  });
  useReleaseDirtyState(isDirty);

  // eslint-disable-next-line react-hooks/incompatible-library
  const newPassword = watch("new_password");
  const policy = evaluatePassword(newPassword);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      changePassword({ current_password: v.current_password, new_password: v.new_password }),
    onSuccess: () => {
      invalidateMe();
      void navigate({ to: "/home" });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearSession();
      invalidateMe();
      void navigate({ to: "/login" });
    },
  });

  function backendError(): FormValidationIssue | null {
    if (!mutation.error) return null;
    return apiErrorFormIssue(mutation.error, {
      id: "change-password-server-error",
      fallbackMessage: t("common.apiError.fallback"),
      domainRules: [
        {
          code: "invalid-current-password",
          status: 401,
          detail: "invalid current password",
          message: t("changePassword.invalidCurrent"),
        },
        {
          code: "password-policy",
          status: 400,
          matches: (detail) => isPolicyDetail(detail),
          message: t("changePassword.policy.error"),
        },
      ],
    });
  }
  const backendPresentation = backendError();
  const issues: FormValidationIssue[] = [
    ...(errors.current_password
      ? [
          {
            id: "current_password-error",
            message: t("changePassword.required"),
            kind: "field" as const,
            controlId: "current_password",
          },
        ]
      : []),
    ...(errors.new_password
      ? [
          {
            id: "new_password-error",
            message: t("changePassword.policy.error"),
            kind: "field" as const,
            controlId: "new_password",
          },
        ]
      : []),
    ...(errors.confirm
      ? [
          {
            id: "confirm-error",
            message: t("changePassword.mismatch"),
            kind: "field" as const,
            controlId: "confirm",
          },
        ]
      : []),
    ...(backendPresentation ? [backendPresentation] : []),
  ];

  const card = (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t("changePassword.title")}</CardTitle>
        {user && (
          <p className="text-muted-foreground text-sm" data-testid="change-password-identity">
            {t("changePassword.loggedInAs", {
              name: user.display_name,
              email: user.web_email ?? "—",
            })}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate>
          <Field
            id="current_password"
            label={t("changePassword.current")}
            error={errors.current_password ? t("changePassword.required") : null}
            register={register("current_password")}
            autoComplete="current-password"
          />
          <Field
            id="new_password"
            label={t("changePassword.next")}
            error={errors.new_password ? t("changePassword.policy.error") : null}
            register={register("new_password")}
            autoComplete="new-password"
            describedBy={
              errors.new_password ? "new_password-error password-policy" : "password-policy"
            }
          />
          <PolicyChecklist result={policy} />
          <Field
            id="confirm"
            label={t("changePassword.confirm")}
            error={errors.confirm ? t("changePassword.mismatch") : null}
            register={register("confirm")}
            autoComplete="new-password"
          />
          <FormErrorSummary
            issues={issues}
            countLabel={(count) => t("validation.fieldErrorCount", { count })}
            data-testid="change-password-error-summary"
          />
          <div className="flex gap-2">
            {forced ? (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                data-testid="change-password-logout"
                disabled={logoutMutation.isPending}
                onClick={() => logoutMutation.mutate()}
              >
                {t("changePassword.logout")}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                data-testid="change-password-cancel"
                onClick={() => void navigate({ to: "/settings" })}
              >
                {t("changePassword.cancel")}
              </Button>
            )}
            <Button type="submit" className="flex-1" disabled={mutation.isPending}>
              {mutation.isPending ? t("changePassword.submitting") : t("changePassword.submit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );

  // Forced change (auth guard) — standalone centered card without the
  // nav rail, since the guard blocks navigation anyway. Voluntary change
  // (from /settings) keeps the full app shell so the user stays oriented.
  if (forced) {
    return <div className="flex min-h-screen items-center justify-center p-4">{card}</div>;
  }

  return (
    <AppShell>
      <div className="flex flex-1 items-start justify-center">{card}</div>
    </AppShell>
  );
}

function isPolicyDetail(detail: unknown): boolean {
  if (typeof detail !== "object" || detail === null) return false;
  const code = (detail as { code?: unknown }).code;
  return code === "password_policy";
}

interface PolicyChecklistProps {
  result: PasswordPolicyResult;
}

function PolicyChecklist({ result }: PolicyChecklistProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      id="password-policy"
      data-testid="password-policy-checklist"
      className="bg-muted/40 ring-border/60 space-y-2 rounded-md px-3 py-2.5 text-xs ring-1"
    >
      <p className="text-muted-foreground text-[11px] font-semibold tracking-wide uppercase">
        {t("changePassword.policy.heading")}
      </p>
      <Rule ok={result.lengthOk} label={t("changePassword.policy.length")} testid="policy-length" />
      <Rule
        ok={result.classesOk}
        label={t("changePassword.policy.minClasses")}
        testid="policy-classes"
        meta={`${result.classCount}/${MIN_CLASSES}`}
      />
      <ul className="text-muted-foreground ml-5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <ClassChip
          ok={result.classes.lower}
          label={t("changePassword.policy.lower")}
          testid="policy-class-lower"
        />
        <ClassChip
          ok={result.classes.upper}
          label={t("changePassword.policy.upper")}
          testid="policy-class-upper"
        />
        <ClassChip
          ok={result.classes.digit}
          label={t("changePassword.policy.digit")}
          testid="policy-class-digit"
        />
        <ClassChip
          ok={result.classes.special}
          label={t("changePassword.policy.special")}
          testid="policy-class-special"
        />
      </ul>
    </div>
  );
}

interface RuleProps {
  ok: boolean;
  label: string;
  testid: string;
  meta?: string;
}

function Rule({ ok, label, testid, meta }: RuleProps): React.ReactElement {
  const Icon = ok ? Check : X;
  return (
    <div className="flex items-center gap-2" data-testid={testid} data-ok={ok ? "true" : "false"}>
      <span
        aria-hidden
        className={cn(
          "flex h-4 w-4 items-center justify-center rounded-full",
          ok ? "bg-emerald-500/15 text-emerald-600" : "text-muted-foreground bg-transparent",
        )}
      >
        <Icon className="h-3 w-3" strokeWidth={2.5} />
      </span>
      <span className={cn(ok ? "text-foreground" : "text-muted-foreground")}>{label}</span>
      {meta && (
        <span className="text-muted-foreground ml-auto font-mono text-[11px] tabular-nums">
          {meta}
        </span>
      )}
    </div>
  );
}

interface ClassChipProps {
  ok: boolean;
  label: string;
  testid: string;
}

function ClassChip({ ok, label, testid }: ClassChipProps): React.ReactElement {
  return (
    <li
      data-testid={testid}
      data-ok={ok ? "true" : "false"}
      className={cn(
        "flex items-center gap-1.5 leading-tight",
        ok ? "text-emerald-700" : "text-muted-foreground",
      )}
    >
      <span
        aria-hidden
        className={cn("inline-block h-1.5 w-1.5 rounded-full", ok ? "bg-emerald-500" : "bg-border")}
      />
      {label}
    </li>
  );
}

interface FieldProps {
  id: string;
  label: string;
  error: string | null;
  register: ReturnType<ReturnType<typeof useForm<FormValues>>["register"]>;
  autoComplete?: string;
  describedBy?: string;
}

function Field({
  id,
  label,
  error,
  register,
  autoComplete,
  describedBy,
}: FieldProps): React.ReactElement {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <PasswordInput
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        autoComplete={autoComplete}
        {...register}
      />
      <FormFieldError
        issue={
          error ? { id: `${id}-error`, message: error, kind: "field", controlId: id } : undefined
        }
      />
    </div>
  );
}
